import React, { useEffect, useContext } from 'react';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import IconButton from '@material-ui/core/IconButton';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import Skeleton from '@material-ui/lab/Skeleton';
import usePlans from "../../../hooks/usePlans";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { getTokens } from "../../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    list: { display: "flex", flexDirection: "column", gap: 14 },
    plan: {
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      backgroundColor: t.paper,
      boxShadow: t.shadow.card,
      overflow: "hidden",
    },
    planHead: {
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
      padding: "16px 18px",
      backgroundColor: t.muted,
      borderBottom: `1px solid ${t.line}`,
    },
    planEyebrow: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: t.textSecondary,
    },
    planName: {
      margin: "2px 0 0",
      fontSize: 17,
      fontWeight: 600,
      lineHeight: 1.3,
      color: t.text,
      overflowWrap: "anywhere",
    },
    cardPricing: {
      display: "flex",
      alignItems: "baseline",
      gap: 4,
      whiteSpace: "nowrap",
    },
    price: {
      fontSize: 26,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
      color: t.text,
    },
    period: { fontSize: 13, color: t.textSecondary },
    planBody: { padding: "14px 18px 18px" },
    features: {
      margin: 0,
      padding: 0,
      listStyle: "none",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: "8px 16px",
    },
    feature: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 14,
      color: t.text,
      "& .MuiSvgIcon-root": { fontSize: 17, color: t.status.ok.fg },
    },
    customCard: {
      display: "flex",
      flexWrap: "wrap",
      gap: 10,
      marginTop: 14,
    },
    custom: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "2px 4px",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      fontSize: 14,
      color: t.text,
    },
    select: { marginTop: 16 },
  };
});

export default function Pricing(props) {
  const {
    setFieldValue,
    setActiveStep,
    activeStep,
  } = props;

  const classes = useStyles();
  const [usersPlans, setUsersPlans] = React.useState(3);
  const [connectionsPlans, setConnectionsPlans] = React.useState(3);
  const [storagePlans, setStoragePlans] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const { user, socket } = useContext(AuthContext);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      await loadPlans();
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const companyId = user.companyId;
      const _planList = await getPlanCompany(undefined, companyId);

      const planList = _planList.plan;

      const plans = []

      plans.push({
        title: planList.name,
        planId: planList.id,
        price: planList.amount,
        description: [
          `${planList.users} Usuários`,
          `${planList.connections} Conexão`,
          `${planList.queues} Filas`
        ],
        users: planList.users,
        connections: planList.connections,
        queues: planList.queues,
        buttonText: 'Selecionar',
        buttonVariant: 'outlined',
      })

      // setStoragePlans(data);

      setStoragePlans(plans);
    } catch (e) {
      // toast.error("Não foi possível carregar a lista de registros");
    }
    setLoading(false);
  };

  const [customValuePlans, setCustomValuePlans] = React.useState(49.00);

  const handleChangeAdd = (event, newValue) => {
    if (newValue < 3) return

    const newPrice = 11.00;

    setUsersPlans(newValue);
    setCustomValuePlans(customValuePlans + newPrice);
  }

  const handleChangeMin = (event, newValue) => {
    if (newValue < 3) return

    const newPrice = 11;

    setUsersPlans(newValue);
    setCustomValuePlans(customValuePlans - newPrice);
  }

  const handleChangeConnectionsAdd = (event, newValue) => {
    if (newValue < 3) return
    const newPrice = 20.00;
    setConnectionsPlans(newValue);
    setCustomValuePlans(customValuePlans + newPrice);
  }

  const handleChangeConnectionsMin = (event, newValue) => {
    if (newValue < 3) return
    const newPrice = 20;
    setConnectionsPlans(newValue);
    setCustomValuePlans(customValuePlans - newPrice);
  }

  const money = (v) => Number(v || 0).toLocaleString('pt-br', { minimumFractionDigits: 2 });

  return (
    <div className={classes.list}>
      {loading && storagePlans.length === 0 && (
        <Skeleton variant="rect" height={190} style={{ borderRadius: 8 }} />
      )}
      {!loading && storagePlans.length === 0 && (
        <Typography variant="body2" color="textSecondary" align="center">
          Não foi possível carregar o plano da empresa.
        </Typography>
      )}
      {storagePlans?.map((tier) => (
        <div className={classes.plan} key={tier.title}>
          <div className={classes.planHead}>
            <div style={{ minWidth: 0 }}>
              <div className={classes.planEyebrow}>Plano</div>
              <h3 className={classes.planName}>{tier.title}</h3>
              {tier.subheader && (
                <Typography variant="caption" color="textSecondary">{tier.subheader}</Typography>
              )}
            </div>
            <div className={classes.cardPricing}>
              <span className={classes.price}>
                R${tier.custom ? money(customValuePlans) : money(tier.price)}
              </span>
              <span className={classes.period}>/mês</span>
            </div>
          </div>
          <div className={classes.planBody}>
            <ul className={classes.features}>
              {tier.description.map((line) => (
                <li className={classes.feature} key={line}>
                  <CheckIcon />
                  {line}
                </li>
              ))}
            </ul>

            {tier.custom && (
              <div className={classes.customCard}>
                <div className={classes.custom}>
                  <IconButton
                    aria-label="Menos usuários"
                    size="small"
                    onClick={e => handleChangeMin(e, usersPlans - 1)}
                  >
                    <RemoveIcon fontSize="inherit" />
                  </IconButton>
                  {usersPlans} Usuários
                  <IconButton
                    aria-label="Mais usuários"
                    size="small"
                    onClick={e => handleChangeAdd(e, usersPlans + 1)}
                  >
                    <AddIcon fontSize="inherit" />
                  </IconButton>
                </div>

                <div className={classes.custom}>
                  <IconButton
                    aria-label="Menos conexões"
                    size="small"
                    onClick={(e) => handleChangeConnectionsMin(e, connectionsPlans - 1)}
                  >
                    <RemoveIcon fontSize="inherit" />
                  </IconButton>
                  {connectionsPlans} Conexão
                  <IconButton
                    aria-label="Mais conexões"
                    size="small"
                    onClick={(e) => handleChangeConnectionsAdd(e, connectionsPlans + 1)}
                  >
                    <AddIcon fontSize="inherit" />
                  </IconButton>
                </div>
              </div>
            )}

            <Button
              className={classes.select}
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => {
                if (tier.custom) {
                  setFieldValue("plan", JSON.stringify({
                    users: usersPlans,
                    connections: connectionsPlans,
                    price: customValuePlans
                  }));
                } else {
                  setFieldValue("plan", JSON.stringify(tier));
                }

                setActiveStep(activeStep + 1);
              }
              }
            >
              {tier.buttonText}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
